import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAdminPermissions } from '../hooks/useAdminPermissions.js';

/**
 * AdminRoute component for protecting admin-only routes
 * Handles authentication, admin verification, and permission checking
 */
const AdminRoute = ({ children, requiredPermission = null, requiredPermissions = [] }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { isAdmin, loading, hasPermission, hasAllPermissions } = useAdminPermissions();
  const location = useLocation();

  // Show loading state while authentication and permissions are being checked
  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-300 dark:text-gray-600">
            {!isLoaded ? 'Loading authentication...' : 'Checking admin access...'}
          </p>
        </div>
      </div>
    );
  }

  // Redirect to admin sign-in if not authenticated
  if (!isSignedIn) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin-sign-in?redirect_url=${redirectUrl}`} replace />;
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6 dark:text-gray-600">You don't have admin privileges to access this page.</p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 mr-4 dark:text-white"
          >
            Go Back
          </button>
          <Navigate to="/dashboard" replace />
        </div>
      </div>
    );
  }

  // Check specific permission if required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Insufficient Permissions</h1>
          <p className="text-gray-300 mb-6 dark:text-gray-600">
            You don't have the required permission ({requiredPermission}) to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check multiple permissions if required
  if (requiredPermissions.length > 0 && !hasAllPermissions(requiredPermissions)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Insufficient Permissions</h1>
          <p className="text-gray-300 mb-6 dark:text-gray-600">
            You don't have all the required permissions to access this page.
          </p>
          <p className="text-sm text-gray-400 mb-6">
            Required: {requiredPermissions.join(', ')}
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render the protected content
  return children;
};

export default AdminRoute;
